import React from 'react';
import { WclProgressSnapshot } from '../useWclProgress';

interface WclQueryProgressProps {
    snapshot: WclProgressSnapshot | null;
}

function progressPercent(snapshot: WclProgressSnapshot): number {
    if (snapshot.workTotal > 0) {
        return Math.min(100, (snapshot.workCurrent / snapshot.workTotal) * 100);
    }
    if (snapshot.planned > 0) {
        return Math.min(100, (snapshot.done / snapshot.planned) * 100);
    }
    return 0;
}

const WclQueryProgress: React.FC<WclQueryProgressProps> = ({ snapshot }) => {
    if (!snapshot) {
        return <p className="wcl-progress-caption">Starting…</p>;
    }

    const wclActive = snapshot.done + snapshot.inFlight > 0;
    const planned = Math.max(snapshot.planned, snapshot.done + snapshot.inFlight, 1);
    const workLabel = snapshot.workLabel || 'items';
    const percent = progressPercent(snapshot);
    const displayCurrent =
        snapshot.workTotal > 0 ? Math.min(snapshot.workTotal, Math.max(1, snapshot.workCurrent)) : snapshot.workCurrent;

    let detail = '';
    if (wclActive) {
        detail = `Warcraft Logs ${snapshot.done} / ~${planned}`;
        if (snapshot.inFlight > 0) {
            detail = `${detail} (${snapshot.inFlight} running)`;
        }
        if (snapshot.throttling) {
            detail = `${detail} · waiting on Warcraft Logs`;
        }
    } else if (snapshot.cached > 0) {
        detail = 'Using saved log data';
    } else {
        detail = 'Starting…';
    }

    const heading =
        snapshot.workTotal > 0
            ? `${workLabel[0].toUpperCase()}${workLabel.slice(1)} ${displayCurrent} / ${snapshot.workTotal}`
            : detail;

    return (
        <div className="wcl-progress" aria-live="polite">
            <div
                className="wcl-progress-bar"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(percent)}
            >
                <div className="wcl-progress-fill" style={{ width: `${percent}%` }} />
            </div>
            <p className="wcl-progress-caption">
                {heading}
                {snapshot.workTotal > 0 ? ` · ${detail}` : ''}
            </p>
        </div>
    );
};

export default WclQueryProgress;
